import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { BreadcrumbProvider } from '../contexts/BreadcrumbContext';
import { CaseProvider } from '../contexts/CaseContext';
import { IntakeCompletionProvider } from '../contexts/IntakeCompletionContext';
import { fetchUserCases, type Case } from '../lib/database';
import { buildCaseKey, parseCaseKey } from '../hooks/useCaseKey';
import { Button } from '../ui';

// Layout route for every path under /cases/:caseKey/*. Loads the user's
// cases list once, finds the matching case by case_ref (UNIQUE in the DB),
// and hydrates CaseContext so downstream screens can skip the prop drilling.

export function CaseShell() {
  const { caseKey } = useParams<{ caseKey: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // Stable across renders, which is the property `useReloadCase()` documents.
  // Inline, this was a fresh function every render — fine for the one caller
  // that invokes it from a click handler, wrong for any consumer that puts it
  // in a dependency array, which would then re-run on every render of this
  // shell. Raised on #1105 by the #1094 session.
  const reloadCase = useCallback(() => setReloadTick(t => t + 1), []);

  const parsed = parseCaseKey(caseKey);

  useEffect(() => {
    if (!parsed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Only blank the screen on a FIRST load or a case switch. A refresh
    // (`reloadCase`, bumping reloadTick) must not: `loading` short-circuits to a
    // full-page spinner below, which unmounts <Outlet/> and takes every child's
    // local state with it — half-ticked rosters, half-typed forms, and the
    // "Saved" badge that was the only evidence the save worked. Exactly the
    // failure ClaimsMatrixV2Screen already fixes for its own refetch.
    if (!caseData) setLoading(true);
    setError(null);
    fetchUserCases()
      .then((cases) => {
        if (cancelled) return;
        const match = cases.find((c) => c.case_ref === parsed.slug);
        if (!match) {
          setError('Case not found or you lack access.');
        } else {
          setCaseData(match);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load case');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parsed?.slug, reloadTick]);

  if (!parsed) {
    return <Navigate to="/cases" replace />;
  }

  // Route params change before the loading effect clears the previous case subtree.
  if (loading || (!error && caseData !== null && buildCaseKey(caseData) !== parsed.slug)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy">
        <Loader2Icon className="w-8 h-8 text-amber animate-spin" />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-navy">
        <p className="text-red-400 mb-4">{error || 'Case not available.'}</p>
        <div className="flex items-center gap-3">
          <Button variant="primary" size="md" onClick={() => setReloadTick((n) => n + 1)}>
            Retry
          </Button>
          <Button
            variant="secondary"
            size="md"
            className="border border-navy-lighter text-gray-300 hover:text-white font-semibold"
            onClick={() => navigate('/cases')}
          >
            Back to Cases
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CaseProvider caseData={caseData} reloadCase={reloadCase}>
      {/* Session+matter-scoped intake-completion watcher (F6). Wraps every
          /cases/:caseKey/* route so a completion toast can fire wherever the
          user navigated after dropping a file. */}
      <IntakeCompletionProvider caseId={caseData.id} caseKey={buildCaseKey(caseData)}>
        <BreadcrumbProvider key={caseData.id} caseKey={buildCaseKey(caseData)}>
          <Outlet />
        </BreadcrumbProvider>
      </IntakeCompletionProvider>
    </CaseProvider>
  );
}
